/**
 * As faixas do velocímetro, definidas pelo negócio em 09/10.
 *
 *   vermelho   0      a  90,00 %  → ABAIXO
 *   amarelo   90,01   a 100,00 %  → ATENÇÃO
 *   verde    100,01   a 120,00 %  → NA META
 *
 * Os limites são INCLUSIVOS À ESQUERDA: 90,00 % ainda é vermelho e 100,00 %
 * ainda é amarelo. O verde só começa quando a meta é ultrapassada.
 *
 * Na primeira implementação isso saiu errado (`<` em vez de `<=`) e 90,00 %
 * caía em amarelo. É por isso que `_teste_faixas.mjs` testa 89,99 / 90,00 /
 * 90,01 / 99,99 / 100,00 / 100,01 / 120 e acima de 120, um por um.
 *
 * Consequência a esperar, e que não é defeito: com o corte em 90 %, o painel
 * fica MAJORITARIAMENTE VERMELHO durante quase todo o evento, e só muda de cor
 * na reta final. É o efeito pretendido pela regra.
 */

export type Faixa = {
  /** Limite superior INCLUSIVO da faixa, em proporção da meta. */
  ate: number
  /** Variável CSS, para quando a cor vai em `style`. */
  cor: string
  /** Hexadecimal, para quando vai em atributo SVG. */
  hex: string
  /** A situação por escrito. No velocímetro ela vive só no `aria-label`. */
  rotulo: string
}

export const FAIXAS: Faixa[] = [
  { ate: 0.90, cor: 'var(--critico)', hex: '#d03b3b', rotulo: 'Abaixo' },
  { ate: 1.00, cor: 'var(--atencao)', hex: '#fab219', rotulo: 'Atenção' },
  { ate: Infinity, cor: 'var(--meta)', hex: '#0ca30c', rotulo: 'Na meta' },
]

/** `p <= f.ate` é o que torna o limite inclusivo à esquerda. Não trocar por `<`. */
export function faixaDe(proporcao: number): Faixa {
  const p = Number.isFinite(proporcao) ? proporcao : 0
  return FAIXAS.find(f => p <= f.ate) ?? FAIXAS[FAIXAS.length - 1]!
}

/**
 * A proporção vendido/meta. Existe SÓ para posicionar a agulha e escolher a
 * cor da faixa — nenhum percentual vai à tela como texto. Meta zerada ou
 * ausente devolve 0, não Infinity: divisão nova cadastrada sem meta não pode
 * jogar a agulha no fim da escala.
 */
export function proporcao(vendido: number, meta: number): number {
  if (!Number.isFinite(vendido) || !Number.isFinite(meta) || meta <= 0) return 0
  return vendido / meta
}
